"use client";

import { useEffect, useState, useMemo } from "react";
import Image from "next/image";
import { ExternalLink, Loader2, Link as LinkIcon } from "lucide-react";

interface LinkPreviewProps {
  url: string;
  text?: string;
}

interface PreviewData {
  title: string;
  description: string;
  image: string;
  domain: string;
  type?: "image" | "video";
}

const getFaviconUrl = (domain: string) =>
  `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;

const highlightLinks = (text: string) => {
  const urlRegex = /(\b(?:https?:\/\/|www\.)[^\s]+)/gi;
  const parts = text.split(urlRegex);
  return parts.map((part, i) => {
    const isLink = urlRegex.test(part);
    if (isLink) {
      const href = part.startsWith("http") ? part : `https://${part}`;
      return (
        <a
          key={i}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 underline hover:text-blue-800 break-words"
          aria-label={`External link to ${href}`}
        >
          {part}
        </a>
      );
    }
    return <span key={i}>{part}</span>;
  });
};

const LinkPreview: React.FC<LinkPreviewProps> = ({ url, text }) => {
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [faviconError, setFaviconError] = useState(false);

  useEffect(() => {
    setFaviconError(false);

    if (!url) {
      setLoading(false);
      setPreviewData(null);
      return;
    }

    try {
      new URL(url);
    } catch {
      setError("Invalid URL");
      setLoading(false);
      setPreviewData(null);
      return;
    }

    const fetchPreview = async () => {
      setLoading(true);
      try {
        const domain = new URL(url).hostname.replace("www.", "");
        const isImage = /\.(jpeg|jpg|gif|png|webp)$/i.test(url);
        const isVideo = /\.(mp4|webm|ogg|mov)$/i.test(url);

        if (isImage || isVideo) {
          setPreviewData({
            title: "Media",
            description: isImage ? "Image" : "Video",
            image: url,
            domain,
            type: isImage ? "image" : "video",
          });
          setError(null);
          setLoading(false);
          return;
        }

        const res = await fetch(`/link-preview?url=${encodeURIComponent(url)}`);
        if (!res.ok) {
          console.error("Failed to fetch preview:", res);
          setError("Could not load preview");
          setPreviewData(null);
          setLoading(false);
          return;
        }

        const json = await res.json();

        const {
          title = "No title",
          description = "",
          image = "",
          contentType = undefined,
        } = json;

        setPreviewData({
          title: title,
          description: description,
          image: image,
          domain,
          type: contentType === "video" ? "video" : undefined,
        });
        setError(null);
      } catch (err) {
        console.error(err);
        setError("Could not load preview");
        setPreviewData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchPreview();
  }, [url]);

  const highlightedText = useMemo(
    () => (text ? highlightLinks(text) : null),
    [text]
  );

  return (
    <div className="mt-2 space-y-2" key={url}>
      {text && (
        <p className="text-sm leading-relaxed text-primary">
          {highlightedText}
        </p>
      )}

      {loading ? (
        <div className="border border-border rounded-lg p-3 bg-secondary/30">
          <div className="flex items-center space-x-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Loading preview...</span>
          </div>
        </div>
      ) : error || !previewData ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="border border-border rounded-lg p-3 bg-secondary/30 hover:bg-secondary/50 block"
          aria-label={`External link to ${url}`}
          tabIndex={0}
        >
          <div className="flex items-center space-x-2 text-primary">
            <ExternalLink className="h-4 w-4" />
            <span className="text-sm underline break-all">{url}</span>
          </div>
        </a>
      ) : (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="border border-border rounded-lg overflow-hidden hover:bg-secondary/10 block"
          aria-label={`External link to ${url}`}
          tabIndex={0}
        >
          <div className="flex">
            <div className="flex-grow p-3">
              <div className="text-xs text-muted-foreground mb-1 flex items-center">
                {!faviconError ? (
                  <Image
                    src={getFaviconUrl(previewData.domain)}
                    alt={`Favicon of ${previewData.domain}`}
                    width={16}
                    height={16}
                    className="mr-2"
                    onError={() => setFaviconError(true)}
                  />
                ) : (
                  <LinkIcon size={16} className="mr-2 text-muted-foreground" />
                )}
                {previewData.domain}
              </div>
              <h3 className="font-bold text-sm line-clamp-2 mb-1">
                {previewData.title}
              </h3>
              <p className="text-xs text-muted-foreground line-clamp-2">
                {previewData.description}
              </p>
            </div>
            {previewData.image && (
              <div className="w-24 h-24 relative">
                {previewData.type === "video" ? (
                  <video
                    src={previewData.image}
                    autoPlay
                    muted
                    loop
                    controls
                    className="w-full h-full object-cover"
                  />
                ) : (
                  // Use regular img here instead of Next.js Image:
                  <img
                    src={previewData.image}
                    alt={previewData.title || "Preview image"}
                    width={96}
                    height={96}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                )}
              </div>
            )}
          </div>
        </a>
      )}
    </div>
  );
};

export default LinkPreview;
